<template>
  <SfList no-margin data-testid="assistant-checks-left-sidepane">
    <slot name="before"></slot>
    <div v-if="isFiltered">
      <SfListItem
        item-id="filtered-results"
        background="transparent"
        font-weight="normal"
        cornered
        :selected="selectedCategory === filteredCategory"
        data-heap="assistant-overview-sidepane-filtered"
        @item-click="setSelected(filteredCategory, '')"
      >
        <template #icon>
          <span :class="filterCategoryIconClass">
            <i class="fa fa-list-ul" />
          </span>
        </template>
        <template #default>
          <span class="tw-font-medium">
            {{ I18n.t('inbox.company.filter_results.title') }}
          </span>
        </template>
      </SfListItem>
      <hr class="tw-mt-4 tw-mb-4 tw-bg-sf-neutral-light-1" />
    </div>

    <SfListItem
      v-if="filteredUnreadCheckCount > 0"
      item-id="checks-unread"
      background="transparent"
      font-weight="medium"
      cornered
      :selected="selectedCategory === UNREAD_CHECKS"
      data-heap="assistant-overview-sidepane-unread"
      @item-click="setSelected(UNREAD_CHECKS, '')"
    >
      <template #icon>
        <i class="fa fa-envelope-open" />
      </template>

      {{ I18n.t('assistant_check.page.sections.unread') }}

      <template #right-content>
        <span
          class="
            tw-flex
            tw-items-center
            tw-justify-center
            tw-h-4
            tw-min-w-[16px]
            tw-rounded-full
            tw-px-1
            tw-leading-3
            tw-bg-sf-danger-light-1
            tw-text-sf-snow-lighter
            tw-text-xs
          "
          data-testid="assistant-checks-unread-count"
        >
          {{ filteredUnreadCheckCount }}
        </span>
      </template>
    </SfListItem>
    <SfListItem
      v-if="hasChecks"
      item-id="checks-all"
      background="transparent"
      font-weight="medium"
      cornered
      :selected="selectedCategory === ALL_CHECKS"
      data-heap="assistant-overview-sidepane-all"
      @item-click="setSelected(ALL_CHECKS, '')"
    >
      {{ I18n.t('assistant_check.page.sections.all') }}

      <template #right-content>
        <UnreadIndicator v-if="filteredUnreadCheckCount > 0" />
      </template>
    </SfListItem>
    <SfListItem
      v-if="generalCheckIds.length"
      item-id="checks-general"
      background="transparent"
      font-weight="medium"
      cornered
      :selected="selectedCategory === GENERAL_CHECKS"
      data-heap="assistant-overview-sidepane-general"
      @item-click="setSelected(GENERAL_CHECKS, '')"
    >
      {{ I18n.t('assistant_check.page.sections.general') }}

      <template #right-content>
        <UnreadIndicator v-if="generalUnreadCheckIds.length > 0" />
      </template>
    </SfListItem>
    <SfListItemGroup
      v-for="workflow in hierarchy"
      :key="workflow.id"
      :initially-expanded="true"
      cornered
      :item-id="`checks-${workflow.id}`"
      background="transparent"
      :header="workflowById[workflow.id]?.name"
    >
      <SfListItem
        v-for="context in workflow.children"
        :key="`${workflow.id}-${context.id}`"
        background="transparent"
        cornered
        :selected="
          selectedCategory === `${workflow.id}` &&
          selectedItem === `${context.id}`
        "
        :item-id="`checks-${workflow.id}-${context.id}`"
        data-heap="assistant-overview-sidepane-context"
        @item-click="setSelected(`${workflow.id}`, `${context.id}`)"
      >
        {{ contextById[context.id]?.name }}

        <template #right-content>
          <UnreadIndicator
            v-if="unreadHierarchyByIdCount.contexts[context.id] > 0"
          />
        </template>
      </SfListItem>

      <template #right-content>
        <UnreadIndicator
          v-if="unreadHierarchyByIdCount.workflows[workflow.id] > 0"
        />
      </template>
    </SfListItemGroup>
  </SfList>
</template>

<script setup lang="ts">
import useI18n from '@app/shared/vue/composables/use-I18n'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { filteredCategory } from '../constants'
import {
  UNREAD_CHECKS,
  ALL_CHECKS,
  GENERAL_CHECKS,
} from '@app/application/sf-assistant-checks/utils'
import SfListItemGroup from '@app/shared/vue/components/sf-list/sf-list-item-group.vue'
import { SfListItem } from '@silverfin/ui-components'
import SfList from '@app/shared/vue/components/sf-list/sf-list.vue'
import UnreadIndicator from '@app/shared/vue/components/unread-indicator.vue'

import { useSfAssistantChecksPage } from '../stores/page'

interface Props {
  selectedCategory: string
  selectedItem: string
}

const { selectedCategory } = defineProps<Props>()

const I18n = useI18n()

const sfAssistantChecksPageStore = useSfAssistantChecksPage()
const {
  hasChecks,
  hierarchy,
  generalUnreadCheckIds,
  filteredUnreadCheckCount,
  contextById,
  workflowById,
  generalCheckIds,
  unreadHierarchyByIdCount,
  isFiltered,
} = storeToRefs(sfAssistantChecksPageStore)

const emit =
  defineEmits<{
    select: [string, string]
  }>()

const filterCategoryIconClass = computed(() =>
  selectedCategory === filteredCategory
    ? 'tw-text-sf-gray-darker'
    : 'tw-text-sf-gray-light'
)

function setSelected(category: string, item: string): void {
  emit('select', category, item)
}
</script>
